import { ConnectProto } from "@racoon/api";

/** A 0-10 score column of `AiVerification`, so a misspelled field fails the typecheck. */
type AiScoreField = Extract<keyof ConnectProto.AiVerification, `${string}Score`>;

/**
 * The verdicts of `AiVerification` the photo-recognition list knows about, and the
 * single place their thresholds live: the card pills (ai-verification.badges) and
 * the verdict filter (ListAiVerifications' `score_filters`) both read `badRange`,
 * so a threshold changed here moves both together.
 *
 * Every score is 0-10 and anchored on its own field name, so the bad side has to be
 * declared per field: the top of the scale for the `*_required` / shortage fields
 * (10 = the problem is certain), the bottom for photo validity and service
 * completeness (10 = the good outcome is certain).
 */
export type AiVerdict = {
  /** Score field as it reads on the generated `AiVerification` message (camelCase). */
  field: AiScoreField;
  /** Label when the verdict is bad — the red pill and the filter option. */
  badKey: string;
  /**
   * Label for the opposite verdict. Not rendered while the card shows bad verdicts
   * only; kept so bringing the "all clear" pills back is a one-line change.
   */
  goodKey: string;
  /** Inclusive score range that counts as bad, in the API's own min/max terms. */
  badRange: { min?: number; max?: number };
};

/** Scores at or above this mean the problem is present (`*_required` / shortage). */
const PROBLEM_PRESENT_MIN = 8;
/** Disinfection is flagged only when the model is more certain than for the rest. */
const DISINFECTION_REQUIRED_MIN = 9;
/** Scores at or below this count as a failure (photo validity / service completeness). */
const OUTCOME_FAILED_MAX = 2;

export const AI_VERDICTS: AiVerdict[] = [
  {
    field: "photoValidityScore",
    badKey: "aiVerification.photoValidityBad",
    goodKey: "aiVerification.photoValidityGood",
    badRange: { max: OUTCOME_FAILED_MAX },
  },
  {
    field: "serviceCompletenessScore",
    badKey: "aiVerification.serviceCompletenessBad",
    goodKey: "aiVerification.serviceCompletenessGood",
    badRange: { max: OUTCOME_FAILED_MAX },
  },
  {
    field: "siteDisinfectionRequiredScore",
    badKey: "aiVerification.siteDisinfectionBad",
    goodKey: "aiVerification.siteDisinfectionGood",
    badRange: { min: DISINFECTION_REQUIRED_MIN },
  },
  {
    field: "containersDisinfectionRequiredScore",
    badKey: "aiVerification.containersDisinfectionBad",
    goodKey: "aiVerification.containersDisinfectionGood",
    badRange: { min: DISINFECTION_REQUIRED_MIN },
  },
  {
    field: "containerReplacementRequiredScore",
    badKey: "aiVerification.containerReplacementBad",
    goodKey: "aiVerification.containerReplacementGood",
    badRange: { min: PROBLEM_PRESENT_MIN },
  },
  {
    field: "containerRepairRequiredScore",
    badKey: "aiVerification.containerRepairBad",
    goodKey: "aiVerification.containerRepairGood",
    badRange: { min: PROBLEM_PRESENT_MIN },
  },
  {
    field: "siteRepairRequiredScore",
    badKey: "aiVerification.siteRepairBad",
    goodKey: "aiVerification.siteRepairGood",
    badRange: { min: PROBLEM_PRESENT_MIN },
  },
  {
    field: "containersShortageScore",
    badKey: "aiVerification.containersShortageBad",
    goodKey: "aiVerification.containersShortageGood",
    badRange: { min: PROBLEM_PRESENT_MIN },
  },
];

/** Whether a set score falls in its verdict's bad range. */
export function isBadScore({ badRange: { min, max } }: AiVerdict, score: number): boolean {
  return (min === undefined || score >= min) && (max === undefined || score <= max);
}

/**
 * `score_filters` for the verdicts selected in the filter (by `field`). The filter is
 * single-select since the API ANDs score filters; see aiVerdictRow. `score_filters`
 * takes the proto spelling (`photo_validity_score`), read off the message schema.
 */
export function aiVerdictScoreFilters(selectedFields: string[]) {
  return AI_VERDICTS.filter(({ field }) => selectedFields.includes(field)).map(
    ({ field, badRange }) => ({
      field: ConnectProto.AiVerificationSchema.field[field].name,
      ...badRange,
    }),
  );
}
